import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';
import { LoadStatus } from '../../constants/constants';
import api from '../../services/DataLayer';
import { initializeByOrganization } from './activityReducer';
import { initialize as initializeAssessments } from './assessmentReducer';
import { initializeCustomFrameworks } from './frameworkReducer';

const organizationDetailsDefault = {
  name: '',
  description: '',
  domain: '',
  strategyDesc: '',
  strategyLinks: [],
  personnel: '',
  address: '',
  function: '',
  industry: '',
  service: '',
  documentation: [],
  phone: '',
  businessId: '',
  id: '',
};

const initialState = {
  initialized: false,
  organizations: [],
  organizationLoadStatus: LoadStatus.INITIAL,
  selectedOrganization: {},
  userList: [],
  contactList: [],
};

const sortOrganizations = (a, b) => {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

export const saveOrganization = createAsyncThunk('organizations/saveOrganization', async (organizationInfo) => {
  try {
    const organization = {
      ...organizationDetailsDefault,
      ...organizationInfo,
    };
    organization.id = await api.createOrganization(organization);
    return organization;
  } catch (error) {
    // TODO: error handling
    return undefined;
  }
});

export const updateOrganization = createAsyncThunk('organizations/updateOrganization', async (organizationInfo) => {
  try {
    const _ = await api.updateOrganization(organizationInfo);
    return organizationInfo;
  } catch (error) {
    return undefined;
  }
});

export const selectOrganization = createAsyncThunk(
  'organizations/selectOrganization',
  async (organizationId, { dispatch, getState, rejectWithValue }) => {
    try {
      if (getState().organizations.selectedOrganization.id !== organizationId) {
        const organization = await api.getOrganization(organizationId);
        let userList = [];
        let contactList = [];
        dispatch(initializeCustomFrameworks(organizationId));
        dispatch(initializeAssessments(organizationId));
        dispatch(initializeByOrganization(organizationId));
        try {
          userList = await api.getOrganizationUsers(organizationId);
        } catch (error) {
          // TODO: ensure this is a 404 not found
        }
        try {
          contactList = await api.getOrganizationContacts(organizationId);
        } catch (error) {
          // TODO: ensure this is a 404 not found
        }
        return { organization, userList, contactList };
      }
      return undefined;
    } catch (error) {
      rejectWithValue(error);
    }
  },
);

export const addOrganizationUser = createAsyncThunk(
  'organizations/addOrganizationUser',
  async (info, { getState, rejectWithValue }) => {
    try {
      const userId = await api.updateOrganizationUser(
        getState().organizations.selectedOrganization.id,
        info.details,
        info.role,
      );
      return { ...info.details, id: userId };
    } catch (error) {
      // TODO: proper handling for errors
      return rejectWithValue(error);
    }
  },
);

export const updateOrganizationUser = createAsyncThunk(
  'organizations/updateOrganizationUser',
  async (details, { getState, rejectWithValue }) => {
    try {
      const contactId = await api.updateOrganizationUser(
        getState().organizations.selectedOrganization.id,
        details,
        details.role,
      );
      return { ...details, id: contactId };
    } catch (error) {
      // TODO: proper handling for errors
      return rejectWithValue(error);
    }
  },
);

export const addOrganizationContact = createAsyncThunk(
  'organizations/addOrganizationContact',
  async (details, { getState, rejectWithValue }) => {
    try {
      const contactId = await api.createContact(getState().organizations.selectedOrganization.id, details);
      return { ...details, id: contactId };
    } catch (error) {
      // TODO: proper handling for errors
      return rejectWithValue(error);
    }
  },
);

export const updateOrganizationContact = createAsyncThunk(
  'organizations/updateOrganizationContact',
  async (details, { getState, rejectWithValue }) => {
    try {
      const contactId = await api.updateContact(getState().organizations.selectedOrganization.id, details.id, details);
      return { ...details, id: contactId };
    } catch (error) {
      // TODO: proper handling for errors
      return rejectWithValue(error);
    }
  },
);

export const initialize = createAsyncThunk('organizations/initialize', async (_, { getState }) => {
  if (!getState().organizations.initialized) {
    if (getState().authentication.isSuperUser) {
      return api.getOrganizations();
    }
    const res = await Auth.currentUserInfo();
    const userId = res.attributes.sub;
    return api.getOrganizationsByUser(userId);
  }
  return undefined;
});

export const organizationSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    clearSelectedOrganization: (state, _) => {
      const newState = { ...state };
      newState.selectedOrganization = {};
      return newState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addOrganizationUser.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newList = [...state.userList];
          newList.push(action.payload);
          state.userList = newList;
        }
      })
      .addCase(updateOrganizationUser.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.userList = state.userList.map((user) => (action.payload.id === user.id ? action.payload : user));
        }
      })
      .addCase(addOrganizationContact.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newList = [...state.contactList];
          newList.push(action.payload);
          state.contactList = newList;
        }
      })
      .addCase(updateOrganizationContact.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newList = [...state.contactList];
          const index = state.contactList.findIndex((contact) => contact.id === action.payload.id);
          newList[index] = action.payload;
          state.contactList = newList;
        }
      })
      .addCase(initialize.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.organizations = action.payload.sort(sortOrganizations);
        }
        state.initialized = true;
      })
      .addCase(saveOrganization.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newState = [...state.organizations, action.payload].sort(sortOrganizations);
          state.organizations = newState;
        }
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.selectedOrganization = { ...state.selectedOrganization, ...action.payload };
          const newOrganizations = [...state.organizations];
          const index = state.organizations.findIndex((org) => org.id === action.payload.id);
          newOrganizations[index] = action.payload;
          state.organizations = newOrganizations;
        }
      })
      .addCase(selectOrganization.pending, (state, _) => {
        state.organizationLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(selectOrganization.fulfilled, (state, action) => {
        const newState = { ...state };
        if (action.payload !== undefined) {
          newState.selectedOrganization = action.payload.organization;
          if (action.payload.userList !== undefined) {
            newState.userList = action.payload.userList;
          }
          if (action.payload.contactList !== undefined) {
            newState.contactList = action.payload.contactList;
          }
        }
        newState.organizationLoadStatus = LoadStatus.SUCCESS;
        return newState;
      })
      .addCase('resetState', (_, __) => ({ ...initialState }));
  },
});

const { actions, reducer } = organizationSlice;

export default reducer;

export const { clearSelectedOrganization } = actions;

export const selectOrganizationList = (state) => state.organizations.organizations;
export const selectOrganizationLoadStatus = (state) => state.organizations.organizationLoadStatus;
export const selectSelectedOrganization = (state) => state.organizations.selectedOrganization;
export const selectSelectedOrganizationId = createSelector(selectSelectedOrganization, (org) => org.id, {
  memoizeOptions: {
    equalityCheck: (a, b) => a.id === b.id,
  },
});
export const selectSelectedOrganizationData = createSelector(
  selectSelectedOrganization,
  (org) => ({ organizationId: org.id, organizationName: org.name }),
);
export const selectUserList = (state) => state.organizations.userList;
export const selectContactList = (state) => state.organizations.contactList;
export const selectPersonDetails = createSelector(
  [selectUserList, selectContactList, (_, id) => id],
  (userList, contactList, id) =>
    userList.find((user) => user.id === id) ?? contactList.find((contact) => contact.id === id) ?? id,
);
export const selectNameList = createSelector(
  [selectUserList, selectContactList, (_, idList) => idList],
  (userList, contactList, idList) => {
    const listOfNames = idList.map(
      (personId) =>
        userList.find((user) => user.id === personId)?.firstName ??
        contactList.find((contact) => contact.id === personId)?.name ?? [''],
    );
    return listOfNames.join(', ');
  },
);
const selectUserRoles = (state) => state.authentication.roles;
export const selectUserRole = createSelector(
  [selectUserRoles, selectSelectedOrganization],
  (roles, organization) => (
    roles.find((item) => item.organizationId === organization.id)?.role ?? ''
  ),
);
